import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../utils/db';
import { interpretDesk } from '../utils/deskIntents';
import { playSound } from '../utils/sound';
import { canPlaySound, resolveAvatarState, speechVolume } from '../utils/houseRules';
import { useDb } from './useDb';
import { useAgentSim } from './useAgentSim';
import { useTimers } from './useTimers';
import { useSpokenLine } from './useSpokenLine';
import { useSpecSettings } from '../contexts/SpecSettingsContext';
import type { AgentId, AvatarState, Processing, SoundId } from '../types/anna';
import type { DeskPanelKind } from '../types/db';
import { applyStageEffect } from '../utils/stageEffects';
import type { Activity } from '../types/stage';

export interface StreamLine {
  id: number;
  from: 'anna' | 'jij';
  text: string;
  via?: 'stem' | 'getypt';
  time: string;
}

export interface DeskPanel {
  uid: string;
  kind: DeskPanelKind;
  pinned: boolean;
}

const WORD_MS = 260;
const MAX_PANELS = 3;

function openingLine() {
  const t = db.getState();
  const busy = t.agentTasks.filter((x) => x.status === 'bezig').length;
  const waiting = t.proposals.filter((p) => p.status === 'wacht').length;
  return `${busy} agents zijn aan het werk. ${waiting} dingen wachten op je ok.`;
}

/** Director of the Commandocentrum: conversation, summoned panels, demo moments, live agents. */
export function useDeskStage() {
  const { soundOn } = useSpecSettings();
  const soundRef = useRef(soundOn);
  soundRef.current = soundOn;

  const moments = useDb((t) => t.deskMoments);
  const time = useDb((t) => t.meta.time);
  const stopped = useDb((t) => t.meta.stopped);

  const [momentIndex, setMomentIndex] = useState(0);
  const [epoch, setEpoch] = useState(0);
  const [stream, setStream] = useState<StreamLine[]>([]);
  const [revealed, setRevealed] = useState<{ id: number; words: number } | null>(null);
  const [panels, setPanels] = useState<DeskPanel[]>([]);
  const [activity, setActivity] = useState<Activity>('idle');
  const [thinkingWhere, setThinkingWhere] = useState<Processing>('lokaal');
  const [transient, setTransient] = useState<AvatarState | null>(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [agentPulse, setAgentPulse] = useState<{ agent: AgentId; id: number } | null>(null);
  const [toast, setToast] = useState<{ id: number; text: string; tone: SoundId } | null>(null);

  const lineId = useRef(1);
  const panelUid = useRef(0);
  const { later, clearTimers } = useTimers();
  const voiceOn = useCallback(() => soundRef.current, []);
  const spoken = useSpokenLine(later, voiceOn);

  const sound = useCallback((id: SoundId) => {
    if (soundRef.current && canPlaySound()) playSound(id);
  }, []);

  const showToast = useCallback((text: string, tone: SoundId = 'klaar') => setToast({ id: Date.now(), text, tone }), []);

  const summon = useCallback((kind: DeskPanelKind) => {
    setPanels((prev) => {
      const existing = prev.find((p) => p.kind === kind);
      const rest = prev.filter((p) => p.kind !== kind);
      const next = [{ uid: existing?.uid ?? `p${panelUid.current++}`, kind, pinned: existing?.pinned ?? false }, ...rest];
      // Too many: the oldest unpinned panel returns to Anna.
      while (next.length > MAX_PANELS) {
        const idx = next.map((p) => p.pinned).lastIndexOf(false);
        if (idx <= 0) break;
        next.splice(idx, 1);
      }
      return next;
    });
  }, []);

  const dismiss = useCallback((uid: string) => setPanels((prev) => prev.filter((p) => p.uid !== uid)), []);
  const togglePin = useCallback(
    (uid: string) => setPanels((prev) => prev.map((p) => (p.uid === uid ? { ...p, pinned: !p.pinned } : p))),
    [],
  );

  const push = useCallback((line: Omit<StreamLine, 'id' | 'time'>) => {
    const id = lineId.current++;
    setStream((s) => [...s, { ...line, id, time: db.getState().meta.time }].slice(-40));
    return id;
  }, []);

  const say = useCallback(
    (text: string, panel?: DeskPanelKind) => {
      const id = push({ from: 'anna', text });
      setActivity('speaking');
      setRevealed({ id, words: 0 });
      // Panels arrive while she is still talking, a third into the sentence.
      if (panel) later(() => summon(panel), Math.min(900, text.split(' ').length * WORD_MS * 0.33));
      spoken.speak(text, {
        wordMs: WORD_MS,
        volume: speechVolume(),
        onWord: (words) => setRevealed({ id, words }),
        onDone: () => setActivity((a) => (a === 'speaking' ? 'idle' : a)),
      });
    },
    [later, push, spoken, summon],
  );

  const bargeIn = useCallback(() => {
    spoken.cancel();
    clearTimers();
    setRevealed(null);
    setActivity('idle');
  }, [clearTimers, spoken]);

  const finishRender = useCallback(() => {
    const r = db.getState().renders[0];
    if (!r || r.status === 'klaar') return;
    db.finishRender(r.id);
    sound('klaar');
    setAgentPulse({ agent: 'studio', id: Date.now() });
    say('Het beeld voor de landingspagina is klaar.', 'render');
  }, [say, sound]);

  const runEffect = useCallback(
    (effect?: 'slackPulse' | 'finishRender' | 'overBudget') => {
      if (effect === 'slackPulse') {
        const m = db.getState().slackMessages[0];
        db.slackQuestionArrived(m.id);
        db.setSlack(m.id, 'concept');
        setAgentPulse({ agent: 'slack', id: Date.now() });
        setPulseKey((k) => k + 1);
        sound('meldt');
      }
      if (effect === 'overBudget') {
        db.switchToLocal(db.getState().meta.cloud.budget * 0.8);
        setTransient('bezorgd');
        later(() => setTransient(null), 5000);
      }
    },
    [later, sound],
  );

  const goToMoment = useCallback(
    (index: number) => {
      const m = moments[index];
      bargeIn();
      setMomentIndex(index);
      if (m.id === 'start') {
        db.reset();
        db.setMeta({ mode: 'Vrij' });
        setPanels([]);
        setStream([]);
        setEpoch((e) => e + 1);
        later(() => push({ from: 'anna', text: openingLine() }), 0);
      }
      db.setMeta({ time: m.time });

      if (m.effect === 'finishRender') {
        // Same path as the live simulation finishing on its own.
        const r = db.getState().renders[0];
        if (r.status === 'klaar') say(m.anna ?? '', 'render');
        else finishRender();
        return;
      }

      const speak = () => {
        runEffect(m.effect);
        if (m.anna) say(m.anna, m.panel);
      };
      if (m.owner) {
        push({ from: 'jij', text: m.owner, via: 'stem' });
        setActivity('thinking');
        setThinkingWhere('lokaal');
        later(speak, 700);
      } else {
        later(speak, 200);
      }
    },
    [bargeIn, finishRender, later, moments, push, runEffect, say],
  );

  const next = useCallback(() => goToMoment((momentIndex + 1) % moments.length), [goToMoment, momentIndex, moments.length]);
  const prev = useCallback(
    () => goToMoment((momentIndex - 1 + moments.length) % moments.length),
    [goToMoment, momentIndex, moments.length],
  );

  const ask = useCallback(
    (text: string, via: 'stem' | 'getypt') => {
      bargeIn();
      push({ from: 'jij', text, via });
      const reply = interpretDesk(text, db.getState());
      setActivity('thinking');
      setThinkingWhere(reply.where);
      later(() => {
        if (reply.tone) sound(reply.tone);
        if (reply.concerned) {
          setTransient('bezorgd');
          later(() => setTransient(null), 4000);
        }
        applyStageEffect(reply.effect);
        if (reply.tone === 'geweigerd') showToast('Verzoek vastgelegd in het logboek', 'geweigerd');
        say(reply.say, reply.panel);
      }, 650);
    },
    [bargeIn, later, push, say, showToast, sound],
  );

  const approved = useCallback(
    (agent: AgentId, text: string) => {
      sound('klaar');
      setAgentPulse({ agent, id: Date.now() });
      showToast(text, 'klaar');
    },
    [showToast, sound],
  );

  const setListening = useCallback((on: boolean) => {
    setActivity((a) => (on ? 'listening' : a === 'listening' ? 'idle' : a));
  }, []);

  // Noodstop is house-wide: whichever screen pulled it, the desk falls silent too.
  useEffect(() => {
    if (stopped) bargeIn();
  }, [stopped, bargeIn]);

  const emergencyStop = useCallback(() => db.stop('bureau'), []);
  const resume = useCallback(() => db.resume('bureau'), []);

  useAgentSim(epoch, finishRender);

  // Opening line once per session.
  const greeted = useRef(false);
  useEffect(() => {
    if (greeted.current) return;
    greeted.current = true;
    db.setMeta({ time: moments[0].time, mode: 'Vrij' });
    push({ from: 'anna', text: openingLine() });
  }, [moments, push]);

  const avatarState = resolveAvatarState({ stopped, activity, transient, rest: 'werkt' });

  return {
    moment: moments[momentIndex],
    momentIndex,
    momentCount: moments.length,
    time,
    stream,
    revealed,
    panels,
    activity,
    thinkingWhere,
    avatarState,
    pulseKey,
    agentPulse,
    toast,
    stopped,
    next,
    prev,
    ask,
    bargeIn,
    setListening,
    summon,
    dismiss,
    togglePin,
    approved,
    showToast,
    dismissToast: () => setToast(null),
    emergencyStop,
    resume,
    sound,
  };
}

export type DeskStage = ReturnType<typeof useDeskStage>;
